import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Ban, Loader2, LogOut, ShieldCheck, Clock, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BanAppeal({ user }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [appeal, setAppeal] = useState(null);
  const [reason, setReason] = useState("");
  const [evidence, setEvidence] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const uploadEvidence = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${API}/files/upload?kind=evidence`, { method: "POST", body: form, credentials: "include" });
      const out = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(out.detail));
      setEvidence((prev) => [...prev, { id: out.id, filename: out.original_filename }]);
      toast.success("Evidence attached.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  useEffect(() => {
    fetch(`${API}/gig/appeal/mine`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { setAppeal(d?.appeal || null); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch(`${API}/gig/appeal`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: reason.trim(), evidence_ids: evidence.map((x) => x.id) }),
      });
      const out = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(out.detail));
      setAppeal(out);
      toast.success("Appeal submitted — reviewed within 72 hours.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="ban-appeal-page" className="flex min-h-screen items-center justify-center bg-[#07090e] px-4 py-16">
      <div className="w-full max-w-lg">
        <div className="rounded-2xl border border-red-500/40 bg-[#0f141c] p-10">
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-red-500/10 text-red-500">
              <Ban className="h-6 w-6" />
            </span>
            <div>
              <h1 className="font-display text-2xl font-black uppercase text-slate-50">Account banned</h1>
              <p className="font-mono-tag text-[9px] text-red-400">Rule 01 · no-show confirmed</p>
            </div>
          </div>
          <p className="mt-6 text-sm leading-relaxed text-slate-400">
            A shift you accepted was never attended. Per Rule 01, that's a permanent ban.
            If this was a documented emergency — hospital or police records — you may appeal
            within 72 hours of the ban.
          </p>

          {!loaded ? (
            <div className="mt-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
          ) : appeal ? (
            <div data-testid="appeal-status" className="mt-8 rounded-lg border border-white/10 bg-[#07090e] p-5">
              <div className="flex items-center gap-2">
                {appeal.status === "pending" && <Clock className="h-4 w-4 text-amber-500" />}
                {appeal.status === "approved" && <CheckCircle2 className="h-4 w-4 text-emerald-400" />}
                {appeal.status === "denied" && <XCircle className="h-4 w-4 text-red-400" />}
                <span className={`font-mono-tag text-[10px] ${
                  appeal.status === "pending" ? "text-amber-500" :
                  appeal.status === "approved" ? "text-emerald-400" : "text-red-400"
                }`}>Appeal {appeal.status}</span>
              </div>
              <p className="mt-3 text-sm text-slate-400">"{appeal.reason}"</p>
              {(appeal.evidence || []).length > 0 && (
                <p className="mt-2 font-mono-tag text-[9px] text-slate-500">{appeal.evidence.length} evidence file(s) attached</p>
              )}
              {appeal.status === "approved" && (
                <p className="mt-3 text-xs text-emerald-400">Your account is reinstated. Sign out and back in to continue.</p>
              )}
            </div>
          ) : (
            <form data-testid="appeal-form" onSubmit={submit} className="mt-8">
              <textarea
                data-testid="appeal-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
                minLength={20}
                rows={4}
                placeholder="Describe the emergency and reference the documentation you can provide (min 20 characters)…"
                className="w-full rounded-md border border-white/10 bg-[#07090e] p-4 text-sm text-slate-50 placeholder:text-slate-600 focus:border-red-500/60 focus:outline-none"
              />
              <label
                data-testid="appeal-evidence-upload"
                className="mt-4 flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-white/15 py-3 text-xs text-slate-500 transition-all hover:border-red-500/40 hover:text-slate-300"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {uploading ? "Uploading…" : "Attach evidence (hospital/police documents, PDF or photo)"}
                <input data-testid="appeal-evidence-input" type="file" accept="image/*,.pdf" className="hidden" onChange={uploadEvidence} disabled={uploading || evidence.length >= 5} />
              </label>
              {evidence.length > 0 && (
                <ul data-testid="appeal-evidence-list" className="mt-2 space-y-1">
                  {evidence.map((ev) => (
                    <li key={ev.id} className="flex items-center gap-2 text-xs text-emerald-400">
                      <CheckCircle2 className="h-3.5 w-3.5" /> {ev.filename}
                    </li>
                  ))}
                </ul>
              )}
              <button
                data-testid="appeal-submit-btn"
                type="submit"
                disabled={busy || reason.trim().length < 20}
                className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-md bg-red-500 font-display text-sm font-bold uppercase text-white transition-all hover:bg-red-400 disabled:opacity-50"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                Submit appeal
              </button>            </form>
          )}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <button
            data-testid="appeal-home-btn"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 font-mono-tag text-[10px] text-slate-500 transition-colors hover:text-amber-500"
          >
            <ShieldCheck className="h-4 w-4" /> GuardGig28 rules
          </button>
          <button
            data-testid="appeal-logout-btn"
            onClick={async () => { await logout(); navigate("/"); }}
            className="flex items-center gap-2 font-mono-tag text-[10px] text-slate-500 transition-colors hover:text-red-400"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
